/**
 * SB6-ANA-LAYOUT-01 — saved-layout migration for hard-edge collision.
 *
 * The analysis workspace switched from RGL's default cascading-push
 * collision to hard-edge collision (`preventCollision={true}`). Any
 * saved layout that contains an overlap (typical cause: floating-point
 * rounding during a previous resize gesture under the cascading rules)
 * is illegal under the new rules and must be cleaned up on load,
 * otherwise RGL silently leaves the overlap in place.
 *
 * Locked spec: `.vibe/12_STORY_BOOK_6_FIXES.md` §D2 Q1 — Christian
 * picked option (a): migrate to nearest valid configuration on load
 * AND write the migration back to localStorage so the next session
 * doesn't keep paying the migration cost.
 *
 * Algorithm:
 *   1. Sort entries by stack-order (y, then x) for determinism.
 *   2. Walk the list. For each entry, check whether its rect overlaps
 *      any already-placed entry. If yes, BFS the surrounding grid cells
 *      until a non-colliding (x, y) is found, then place there.
 *   3. Return both the cleaned layouts AND a `changed` flag so the
 *      caller can decide whether to re-persist.
 *
 * The BFS is bounded to a 50×50 cell search around the original
 * position; if no slot is found (which should never happen in practice
 * because the grid is infinite-tall) we leave the entry at its
 * original position and accept the overlap. The default workspace fits
 * in 12 cols × 12 rows; the search budget is comfortably wide.
 */
import type { Layout } from "react-grid-layout";

interface PlacedRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function rectsOverlap(a: PlacedRect, b: PlacedRect): boolean {
  // Two rectangles overlap iff neither is entirely to one side of the
  // other on either axis. `x + w` is the exclusive right edge so an
  // entry at x=2,w=3 occupies columns 2,3,4 — a neighbour at x=5 is
  // adjacent, not overlapping.
  if (a.x + a.w <= b.x) return false;
  if (b.x + b.w <= a.x) return false;
  if (a.y + a.h <= b.y) return false;
  if (b.y + b.h <= a.y) return false;
  return true;
}

function collidesAny(
  candidate: PlacedRect,
  placed: readonly PlacedRect[],
): boolean {
  for (const p of placed) {
    if (rectsOverlap(candidate, p)) return true;
  }
  return false;
}

/**
 * BFS the integer grid starting at `(start.x, start.y)` for the first
 * cell where placing a `w × h` rect produces no collision against
 * `placed`. The search expands ring-by-ring (Chebyshev distance) so
 * the chosen cell is the L∞-closest free spot to the original.
 *
 * `cols` clamps the right edge: `x + w ≤ cols`. The y axis is
 * unbounded (the workspace grows downwards). `radius` caps the search
 * envelope so a pathologically packed layout doesn't loop forever.
 */
function findNearestFreeCell(
  start: PlacedRect,
  placed: readonly PlacedRect[],
  cols: number,
  radius = 50,
): { x: number; y: number } | null {
  const candidate = { ...start };
  // Try the original position first — by far the common case.
  if (!collidesAny(candidate, placed)) return { x: start.x, y: start.y };

  for (let r = 1; r <= radius; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        // Skip interior cells already explored at smaller radii.
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = start.x + dx;
        const y = start.y + dy;
        if (x < 0 || y < 0) continue;
        if (x + start.w > cols) continue;
        const probe = { ...start, x, y };
        if (!collidesAny(probe, placed)) return { x, y };
      }
    }
  }
  return null;
}

export interface LayoutMigrationResult {
  layout: Layout[];
  changed: boolean;
}

/**
 * Migrate one breakpoint's layout array. Caller is responsible for
 * looping over breakpoints. Stable for already-valid input — only
 * mutates the entries that need to move.
 */
export function migrateLayoutForCollision(
  layout: readonly Layout[],
  cols: number,
): LayoutMigrationResult {
  // Stable iteration order so repeat-load produces an idempotent
  // result: top-to-bottom, left-to-right.
  const sorted = [...layout].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
  const placed: PlacedRect[] = [];
  const out: Layout[] = [];
  let changed = false;
  for (const entry of sorted) {
    const rect: PlacedRect = {
      x: entry.x,
      y: entry.y,
      w: entry.w,
      h: entry.h,
    };
    if (!collidesAny(rect, placed)) {
      placed.push(rect);
      out.push(entry);
      continue;
    }
    const slot = findNearestFreeCell(rect, placed, cols);
    if (slot) {
      changed = true;
      const moved: Layout = { ...entry, x: slot.x, y: slot.y };
      placed.push({ ...rect, x: slot.x, y: slot.y });
      out.push(moved);
    } else {
      // No slot in the search envelope — leave the entry at its
      // original spot. Worst case is one overlap survives; the user
      // can fix it by hand. This branch is unreachable for the
      // default 5-widget workspace.
      placed.push(rect);
      out.push(entry);
    }
  }
  return { layout: out, changed };
}

/**
 * Convenience wrapper for the full `Record<bp, Layout[]>` shape RGL
 * uses. `cols` map keyed by breakpoint; entries for unknown bps fall
 * through unchanged.
 */
export function migrateLayoutsForCollision(
  layouts: Record<string, Layout[]>,
  colsByBp: Record<string, number>,
): { layouts: Record<string, Layout[]>; changed: boolean } {
  let anyChanged = false;
  const out: Record<string, Layout[]> = {};
  for (const [bp, arr] of Object.entries(layouts)) {
    const cols = colsByBp[bp] ?? 12;
    const result = migrateLayoutForCollision(arr, cols);
    out[bp] = result.layout;
    if (result.changed) anyChanged = true;
  }
  return { layouts: out, changed: anyChanged };
}
